import { useState } from "react";
import { Box, Typography, Avatar, IconButton } from "@mui/material";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import Tooltip from '@mui/material/Tooltip';

function UploadPhoto({ setImageSrc, imageSrc, uploadImage }) {
  const [isDragActive, setIsDragActive] = useState(false);

  const MAX_FILE_SIZE_MB = 5;

  const processFile = (file) => {
    if (!file) return;

    const fileSizeInMB = file.size / (1024 * 1024);
    if (fileSizeInMB > MAX_FILE_SIZE_MB) {
      alert(`File size exceeds the limit of ${MAX_FILE_SIZE_MB}MB.`);
      return;
    }

    let reader = new FileReader();
    reader.onloadend = (readerEvt) => {
      let binaryString = readerEvt.target.result;
      uploadImage(binaryString);
      setImageSrc(binaryString);
    };
    reader.readAsDataURL(file);
  };

  const handleImageChange = (e) => {
    e.preventDefault();
    processFile(e.target.files[0]);
    e.target.value = '';
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragActive) setIsDragActive(true);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFile(e.dataTransfer.files[0]);
    }
  };

  const clearImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setImageSrc(null);
  };

  return (
    <Box 
      component="label" 
      sx={{ 
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        border: '2px dashed',
        borderColor: isDragActive ? 'primary.main' : 'grey.300',
        borderRadius: 2,
        height: '100%',
        minHeight: '120px',
        bgcolor: isDragActive ? 'rgba(25, 118, 210, 0.08)' : 'background.paper',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          borderColor: 'primary.main',
          bgcolor: 'rgba(25, 118, 210, 0.04)',
        }
      }}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <input
        type="file"
        accept="image/*"
        onChange={handleImageChange}
        style={{ display: 'none' }}
      />
      
      {imageSrc ? (
        <>
          <Avatar
            src={imageSrc}
            variant="square"
            sx={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute' }}
          />
          <Tooltip title="Remove Image" placement="bottom">
          <IconButton 
            onClick={clearImage}
            sx={{ 
              position: 'absolute', 
              top: 8, 
              right: 8, 
              bgcolor: 'rgba(0,0,0,0.5)', 
              color: 'white',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
            }}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          </Tooltip>
        </>
      ) : (
        <Box sx={{ textAlign: 'center', p: 2 }}>
          <CloudUploadIcon sx={{ fontSize: 40, color: isDragActive ? 'primary.main' : 'text.secondary', mb: 1, transition: 'color 0.2s ease' }} />
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            Click or drag an image here
          </Typography>
          <Typography variant="caption" color="text.disabled">
            Supports: JPG, PNG, JPEG
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default UploadPhoto;

